import type { PipelineSnapshot, Alert } from '../../hooks/usePipelineSnapshot';

// Sections 6.1, 6.2, 6.3 of the spec compressed into a single sidebar.
// 6.4 Provider Health is deferred to v2 (codex review: Vertex section is initialized but not polled).

interface Props {
  snapshot: PipelineSnapshot;
}

function alertColor(severity: Alert['severity']): string {
  return severity === 'critical' ? 'bg-red-50 border-red-300 text-red-700' : 'bg-amber-50 border-amber-300 text-amber-700';
}

export function SystemHealthSidebar({ snapshot }: Props) {
  const { workerActive, workerConfig, queues, alerts } = snapshot;
  const queueColor =
    queues.asyncJobsPending > 200 || (queues.subBatchesProcessing === 0 && queues.subBatchesPending > 0)
      ? 'text-red-600'
      : queues.asyncJobsPending > 50
        ? 'text-amber-600'
        : 'text-green-600';

  return (
    <aside className="w-full lg:w-[280px] flex-shrink-0 space-y-3">
      {/* Alerts */}
      <div className="rounded-xl border bg-white shadow-sm p-3">
        <div className="text-xs font-semibold mb-2">Alerts</div>
        {alerts.length === 0 ? (
          <div className="text-xs text-green-600">All clear</div>
        ) : (
          <div className="space-y-2">
            {alerts.map(a => (
              <div key={a.name} className={`rounded-md border p-2 text-xs ${alertColor(a.severity)}`}>
                <div className="font-semibold uppercase tracking-wider text-[10px]">{a.name}</div>
                <div className="mt-1 text-[11px]">{a.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Workers */}
      <div className="rounded-xl border bg-white shadow-sm p-3">
        <div className="text-xs font-semibold mb-2">Workers</div>
        <dl className="space-y-1 text-xs">
          <div className="flex justify-between">
            <dt className="text-gray-500">SubBatchProcessor</dt>
            <dd className="font-mono">{workerActive.subBatchesProcessing} / {workerConfig.parallelSubBatches}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">AEO transitions</dt>
            <dd className="font-mono">{workerActive.aeoInFlight} / {workerConfig.maxAeoTransitions}</dd>
          </div>
        </dl>
      </div>

      {/* Queue depth */}
      <div className="rounded-xl border bg-white shadow-sm p-3">
        <div className="text-xs font-semibold mb-2">Queue depth</div>
        <dl className="space-y-1 text-xs">
          <div className="flex justify-between">
            <dt className="text-gray-500">async_jobs pending</dt>
            <dd className={`font-mono ${queueColor}`}>{queues.asyncJobsPending}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">async_jobs processing</dt>
            <dd className="font-mono">{queues.asyncJobsProcessing}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Sub-batches pending</dt>
            <dd className="font-mono">{queues.subBatchesPending}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Sub-batches processing</dt>
            <dd className="font-mono">{queues.subBatchesProcessing}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Translations active</dt>
            <dd className="font-mono">{queues.translationPending}</dd>
          </div>
        </dl>
      </div>

      {/* Translations panel */}
      {snapshot.translationJobs.length > 0 && (
        <div className="rounded-xl border bg-white shadow-sm p-3">
          <div className="text-xs font-semibold mb-2">Active translations</div>
          <ul className="space-y-1 text-xs">
            {snapshot.translationJobs.slice(0, 8).map(t => {
              const pct = t.totalItems > 0 ? Math.round((t.completedItems / t.totalItems) * 100) : 0;
              return (
                <li key={t.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">
                    <span className="font-medium">{t.siteDomain}</span>{' '}
                    <span className="text-gray-500">{t.targetLanguage}</span>
                  </span>
                  <span className="font-mono text-gray-600">{pct}%</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </aside>
  );
}
